import React from 'react';

interface Props {
  variant: number;
  accent: string;
}

/* ─── helper: shared browser chrome ─────────────────────────── */
const BrowserChrome = () => (
  <>
    <rect x="12" y="10" width="296" height="160" rx="7" fill="#0f1629" stroke="#1e2d47" strokeWidth="1" />
    <rect x="12" y="10" width="296" height="26" rx="7" fill="#13203a" />
    <rect x="12" y="28" width="296" height="8" fill="#13203a" />
    <circle cx="27" cy="23" r="3.5" fill="#ff5f57" fillOpacity="0.75" />
    <circle cx="37" cy="23" r="3.5" fill="#febc2e" fillOpacity="0.75" />
    <circle cx="47" cy="23" r="3.5" fill="#28c840" fillOpacity="0.75" />
    <rect x="58" y="16" width="180" height="13" rx="3" fill="#0a1020" stroke="#1e2d47" strokeWidth="0.5" />
  </>
);

/* ─── helper: shared phone chrome ───────────────────────────── */
const PhoneChrome = () => (
  <>
    <rect x="110" y="8" width="100" height="164" rx="16" fill="#0f1629" stroke="#1e2d47" strokeWidth="1" />
    <rect x="110" y="8" width="100" height="18" rx="16" fill="#13203a" />
    <rect x="110" y="18" width="100" height="8" fill="#13203a" />
    <rect x="142" y="8" width="36" height="10" rx="5" fill="#080d16" />
    <rect x="142" y="166" width="36" height="3" rx="2" fill="#1e2d47" />
  </>
);

/* ─── 0: E-commerce ──────────────────────────────────────────── */
const Ecommerce = ({ a }: { a: string }) => (
  <svg viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
    <rect width="320" height="180" fill="#080d16" />
    <BrowserChrome />
    {/* navbar */}
    <rect x="20" y="44" width="280" height="18" rx="3" fill="#13203a" />
    <rect x="26" y="49" width="30" height="8" rx="2" fill={a} fillOpacity="0.9" />
    <rect x="180" y="50" width="20" height="6" rx="2" fill="#1e2d47" />
    <rect x="206" y="50" width="22" height="6" rx="2" fill="#1e2d47" />
    <rect x="234" y="50" width="22" height="6" rx="2" fill="#1e2d47" />
    <rect x="264" y="47" width="28" height="12" rx="6" fill={a} fillOpacity="0.3" />
    {/* hero */}
    <rect x="20" y="68" width="280" height="26" rx="3" fill="#0c1520" />
    <rect x="28" y="74" width="60" height="7" rx="2" fill="#e2e8f0" fillOpacity="0.8" />
    <rect x="28" y="84" width="100" height="4" rx="2" fill="#94a3b8" fillOpacity="0.4" />
    <rect x="244" y="71" width="48" height="20" rx="4" fill={a} fillOpacity="0.22" stroke={a} strokeWidth="0.5" strokeOpacity="0.4" />
    {/* product cards */}
    {[0, 1, 2].map((i) => (
      <g key={i}>
        <rect x={20 + i * 98} y="100" width="88" height="62" rx="4" fill="#0f1a2e" stroke="#1e2d47" strokeWidth="0.5" />
        <rect x={26 + i * 98} y="106" width="76" height="30" rx="3" fill={a} fillOpacity={0.12 - i * 0.02} />
        <rect x={26 + i * 98} y="140" width={40 + i * 4} height="5" rx="2" fill="#94a3b8" fillOpacity="0.55" />
        <rect x={26 + i * 98} y="149" width="24" height="7" rx="3" fill={a} fillOpacity="0.7" />
      </g>
    ))}
  </svg>
);

/* ─── 1: Fitness mobile ──────────────────────────────────────── */
const Fitness = ({ a }: { a: string }) => (
  <svg viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
    <rect width="320" height="180" fill="#080d16" />
    <PhoneChrome />
    <rect x="118" y="32" width="84" height="7" rx="2" fill="#e2e8f0" fillOpacity="0.85" />
    <rect x="128" y="43" width="64" height="5" rx="2" fill="#94a3b8" fillOpacity="0.45" />
    {/* ring */}
    <circle cx="160" cy="93" r="28" fill="none" stroke="#1e2d47" strokeWidth="9" />
    <circle cx="160" cy="93" r="28" fill="none" stroke={a} strokeWidth="9"
      strokeDasharray="132" strokeDashoffset="34" strokeLinecap="round"
      transform="rotate(-90 160 93)" />
    <text x="160" y="90" textAnchor="middle" fill="#e2e8f0" fontSize="11" fontWeight="700" fontFamily="Inter, sans-serif">75%</text>
    <text x="160" y="100" textAnchor="middle" fill="#94a3b8" fontSize="7" fontFamily="Inter, sans-serif">goal</text>
    {/* stat chips */}
    {[0, 1, 2].map((i) => (
      <g key={i}>
        <rect x={118 + i * 30} y="128" width="25" height="26" rx="4" fill="#13203a" />
        <rect x={120 + i * 30} y="130" width="21" height="6" rx="2" fill={a} fillOpacity={0.55 - i * 0.1} />
        <rect x={120 + i * 30} y="139" width="16" height="4" rx="2" fill="#94a3b8" fillOpacity="0.35" />
        <rect x={120 + i * 30} y="145" width="12" height="3" rx="2" fill="#94a3b8" fillOpacity="0.25" />
      </g>
    ))}
  </svg>
);

/* ─── 2: Analytics dashboard ─────────────────────────────────── */
const Analytics = ({ a }: { a: string }) => {
  const barHeights = [30, 44, 22, 50, 36, 54, 28, 42];
  return (
    <svg viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
      <rect width="320" height="180" fill="#080d16" />
      <BrowserChrome />
      {/* sidebar */}
      <rect x="20" y="44" width="50" height="118" rx="3" fill="#0c1520" stroke="#1e2d47" strokeWidth="0.5" />
      <rect x="26" y="52" width="38" height="7" rx="2" fill={a} fillOpacity="0.3" />
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x="26" y={64 + i * 12} width={32 - i * 4} height="5" rx="2" fill="#1e2d47" />
      ))}
      {/* kpi cards */}
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x={78 + i * 68} y="44" width="62" height="34" rx="3" fill="#0c1520" stroke="#1e2d47" strokeWidth="0.5" />
          <rect x={84 + i * 68} y="50" width="36" height="4" rx="2" fill="#94a3b8" fillOpacity="0.45" />
          <rect x={84 + i * 68} y="58" width={44 - i * 4} height="10" rx="2" fill={a} fillOpacity={0.75 - i * 0.15} />
          <rect x={84 + i * 68} y="70" width="22" height="4" rx="2" fill="#28c840" fillOpacity="0.5" />
        </g>
      ))}
      {/* bar chart */}
      <rect x="78" y="84" width="230" height="72" rx="3" fill="#0c1520" stroke="#1e2d47" strokeWidth="0.5" />
      <rect x="86" y="90" width="60" height="5" rx="2" fill="#e2e8f0" fillOpacity="0.55" />
      <line x1="86" y1="150" x2="300" y2="150" stroke="#1e2d47" strokeWidth="0.5" />
      {barHeights.map((h, i) => (
        <rect key={i} x={90 + i * 26} y={150 - h} width="18" height={h} rx="3"
          fill={a} fillOpacity={0.28 + i * 0.05} />
      ))}
    </svg>
  );
};

/* ─── 3: Real estate ─────────────────────────────────────────── */
const RealEstate = ({ a }: { a: string }) => (
  <svg viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
    <rect width="320" height="180" fill="#080d16" />
    <BrowserChrome />
    {/* search bar */}
    <rect x="20" y="44" width="220" height="18" rx="4" fill="#0c1520" stroke="#1e2d47" strokeWidth="0.5" />
    <rect x="26" y="49" width="80" height="6" rx="2" fill="#334155" fillOpacity="0.55" />
    <rect x="246" y="44" width="62" height="18" rx="4" fill={a} fillOpacity="0.2" stroke={a} strokeWidth="0.5" strokeOpacity="0.45" />
    <rect x="254" y="49" width="46" height="6" rx="2" fill={a} fillOpacity="0.65" />
    {/* map */}
    <rect x="20" y="68" width="152" height="94" rx="4" fill="#0b1828" stroke="#1e2d47" strokeWidth="0.5" />
    {[90, 112, 134].map((y) => <line key={y} x1="20" y1={y} x2="172" y2={y} stroke="#1e2d47" strokeWidth="0.4" />)}
    {[60, 110, 148].map((x) => <line key={x} x1={x} y1="68" x2={x} y2="162" stroke="#1e2d47" strokeWidth="0.4" />)}
    <circle cx="76" cy="95" r="5" fill={a} fillOpacity="0.9" />
    <circle cx="76" cy="95" r="10" fill={a} fillOpacity="0.12" />
    <circle cx="126" cy="118" r="5" fill={a} fillOpacity="0.7" />
    <circle cx="126" cy="118" r="10" fill={a} fillOpacity="0.1" />
    <circle cx="54" cy="130" r="4" fill={a} fillOpacity="0.5" />
    {/* listing cards */}
    {[0, 1].map((i) => (
      <g key={i}>
        <rect x="178" y={68 + i * 50} width="130" height="44" rx="4" fill="#0c1520" stroke="#1e2d47" strokeWidth="0.5" />
        <rect x="184" y={74 + i * 50} width="40" height="32" rx="3" fill={a} fillOpacity={0.14 - i * 0.04} />
        <rect x="230" y={74 + i * 50} width="66" height="6" rx="2" fill="#e2e8f0" fillOpacity="0.7" />
        <rect x="230" y={84 + i * 50} width="48" height="5" rx="2" fill="#94a3b8" fillOpacity="0.45" />
        <rect x="230" y={93 + i * 50} width="36" height="7" rx="3" fill={a} fillOpacity={0.65 - i * 0.15} />
      </g>
    ))}
  </svg>
);

/* ─── 4: Messenger PWA ───────────────────────────────────────── */
const Messenger = ({ a }: { a: string }) => (
  <svg viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
    <rect width="320" height="180" fill="#080d16" />
    <PhoneChrome />
    <circle cx="130" cy="36" r="9" fill={a} fillOpacity="0.2" />
    <rect x="143" y="31" width="36" height="6" rx="2" fill="#e2e8f0" fillOpacity="0.8" />
    <rect x="143" y="41" width="22" height="4" rx="2" fill="#28c840" fillOpacity="0.55" />
    <line x1="110" y1="52" x2="210" y2="52" stroke="#1e2d47" strokeWidth="0.5" />
    {/* received */}
    <rect x="116" y="57" width="62" height="16" rx="8" fill="#13203a" stroke="#1e2d47" strokeWidth="0.5" />
    <rect x="122" y="61" width="44" height="5" rx="2" fill="#94a3b8" fillOpacity="0.45" />
    <rect x="122" y="68" width="30" height="3" rx="2" fill="#94a3b8" fillOpacity="0.25" />
    {/* sent */}
    <rect x="150" y="79" width="54" height="16" rx="8" fill={a} fillOpacity="0.22" />
    <rect x="156" y="83" width="40" height="5" rx="2" fill={a} fillOpacity="0.65" />
    <rect x="156" y="90" width="26" height="3" rx="2" fill={a} fillOpacity="0.35" />
    {/* received */}
    <rect x="116" y="101" width="70" height="20" rx="8" fill="#13203a" stroke="#1e2d47" strokeWidth="0.5" />
    <rect x="122" y="105" width="50" height="5" rx="2" fill="#94a3b8" fillOpacity="0.45" />
    <rect x="122" y="113" width="36" height="3" rx="2" fill="#94a3b8" fillOpacity="0.25" />
    {/* sent */}
    <rect x="140" y="127" width="64" height="16" rx="8" fill={a} fillOpacity="0.18" />
    <rect x="146" y="131" width="48" height="5" rx="2" fill={a} fillOpacity="0.65" />
    <rect x="146" y="138" width="28" height="3" rx="2" fill={a} fillOpacity="0.35" />
    {/* input */}
    <rect x="114" y="148" width="80" height="14" rx="7" fill="#13203a" stroke="#1e2d47" strokeWidth="0.5" />
    <rect x="120" y="153" width="38" height="4" rx="2" fill="#334155" fillOpacity="0.45" />
    <circle cx="191" cy="155" r="7" fill={a} fillOpacity="0.28" />
  </svg>
);

/* ─── 5: Restaurant app ──────────────────────────────────────── */
const Restaurant = ({ a }: { a: string }) => (
  <svg viewBox="0 0 320 180" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
    <rect width="320" height="180" fill="#080d16" />
    <PhoneChrome />
    {/* header */}
    <rect x="110" y="26" width="100" height="24" fill="#13203a" />
    <rect x="120" y="31" width="50" height="7" rx="2" fill="#e2e8f0" fillOpacity="0.85" />
    <rect x="174" y="30" width="26" height="18" rx="4" fill={a} fillOpacity="0.18" />
    {/* tabs */}
    <rect x="114" y="55" width="28" height="10" rx="5" fill={a} fillOpacity="0.75" />
    <rect x="146" y="55" width="24" height="10" rx="5" fill="#13203a" stroke="#1e2d47" strokeWidth="0.5" />
    <rect x="174" y="55" width="28" height="10" rx="5" fill="#13203a" stroke="#1e2d47" strokeWidth="0.5" />
    {/* menu rows */}
    {[0, 1, 2, 3].map((i) => (
      <g key={i}>
        <rect x="114" y={70 + i * 22} width="82" height="18" rx="4" fill="#0c1520" stroke="#1e2d47" strokeWidth="0.5" />
        <rect x="118" y={74 + i * 22} width="14" height="10" rx="2" fill={a} fillOpacity={0.18 + i * 0.04} />
        <rect x="136" y={75 + i * 22} width={32 + i * 3} height="4" rx="2" fill="#e2e8f0" fillOpacity="0.65" />
        <rect x="136" y={82 + i * 22} width="20" height="4" rx="2" fill={a} fillOpacity="0.55" />
        <circle cx="187" cy={79 + i * 22} r="6" fill={a} fillOpacity="0.18" stroke={a} strokeWidth="0.5" strokeOpacity="0.45" />
        <rect x={184} y={78.25 + i * 22} width="6" height="1.5" rx="1" fill={a} fillOpacity="0.8" />
        <rect x={185.75} y={76.5 + i * 22} width="1.5" height="5" rx="1" fill={a} fillOpacity="0.8" />
      </g>
    ))}
    {/* cart bar */}
    <rect x="114" y="160" width="82" height="6" rx="3" fill={a} fillOpacity="0.22" />
  </svg>
);

const MOCKUPS = [Ecommerce, Fitness, Analytics, RealEstate, Messenger, Restaurant];

const ProjectMockup: React.FC<Props> = ({ variant, accent }) => {
  const Comp = MOCKUPS[variant] ?? MOCKUPS[0];
  return <Comp a={accent} />;
};

export default ProjectMockup;
